import { Component, ViewChild } from '@angular/core';
import { take } from 'rxjs/operators';
import { GridButton } from 'src/app/core/models/grid-button.model';
import { GridColumn } from 'src/app/core/models/grid-column.model';
import { GridSearchFilter } from 'src/app/core/models/grid-search-filter.model';
import { GridSelectedFilter } from 'src/app/core/models/grid-selected-filter.model';
import { GridComponent } from 'src/app/core/widgets/grid/grid.component';
import { DeliveryNoteService } from '../../services/delivery-note.service';
import { NoteStatus, TYPE_LABELS, statusBadge, ukDate } from '../../models/delivery-note.models';

/** Every delivery note, filtered by status like the quotes list. */
@Component({
    selector: 'app-delivery-notes',
    templateUrl: './delivery-notes.component.html',
    standalone: false
})
export class DeliveryNotesComponent {
  @ViewChild(GridComponent, { static: false }) grid: GridComponent;

  status: string = '';
  error: string = '';

  statusButtons = [
    { value: '', label: 'All', icon: 'fas fa-fw fa-truck-loading' },
    { value: 'draft', label: 'Draft', icon: 'fas fa-pencil-alt' },
    { value: 'ready', label: 'Ready to pick', icon: 'fas fa-clipboard-list' },
    { value: 'part_picked', label: 'Part picked', icon: 'fas fa-adjust' },
    { value: 'picked', label: 'Picked', icon: 'fas fa-box' },
    { value: 'despatched', label: 'Despatched', icon: 'fas fa-shipping-fast' },
    { value: 'cancelled', label: 'Cancelled', icon: 'fas fa-ban' },
  ];

  buttons: GridButton[] = [
    { label: '<i class="fas fa-pencil-alt"></i>', link: '/delivery-notes/edit/:id', type: 'btn-warning' } as GridButton,
    { label: '<i class="fas fa-clipboard-check"></i> Pick', link: '/picking-slips/:id', type: 'btn-info',
      trigger: (r) => r.status === 'ready' || r.status === 'part_picked' } as GridButton,
    { label: '<i class="fas fa-file-pdf"></i>', type: 'btn-secondary', action: (b, r) => this.print(r) } as GridButton,
  ];

  columns: GridColumn[] = [
    { active: true, label: 'Number', field: 'noteNumber' } as GridColumn,
    { active: true, label: 'Type', field: 'noteType', value: (v) => TYPE_LABELS[v] || v } as GridColumn,
    { active: true, label: 'Customer', field: 'account.name', value: (v, r) => r.account ? r.account.name : '' } as GridColumn,
    { active: true, label: 'Customer ref', field: 'customerRef', value: (v) => v || '' } as GridColumn,
    { active: true, label: 'Required', field: 'requiredDate', value: (v) => ukDate(v) } as GridColumn,
    { active: true, label: 'Status', field: 'status', value: (v: NoteStatus) => statusBadge(v) } as GridColumn,
    { active: true, label: 'Despatched', field: 'despatchedAt', value: (v) => v ? ukDate(String(v).slice(0, 10)) : '' } as GridColumn,
    { active: true, label: 'Created', field: 'createdAt', value: (v) => ukDate(String(v).slice(0, 10)) } as GridColumn,
  ];

  searchFields: string[] = ['noteNumber', 'customerRef', 'account.name', 'deliveryName', 'deliveryPostCode'];
  searchFilters: GridSearchFilter[] = [
    { field: 'noteNumber', label: 'Number' } as GridSearchFilter,
    { field: 'account.name', label: 'Customer' } as GridSearchFilter,
    { field: 'customerRef', label: 'Customer ref' } as GridSearchFilter,
    { field: 'requiredDate', label: 'Required', type: 'date' } as GridSearchFilter,
  ];

  constructor(private service: DeliveryNoteService) { }

  setStatus(status: string): void {
    this.status = status;
    this.grid.selectedFilters = status ? [{ condition: 'eq', field: 'status', value: status } as GridSelectedFilter] : [];
    this.grid.getRecordsForEntity();
    this.grid.onValueChanged();
  }

  print(record: any): void {
    this.error = '';
    this.service.pdf(record.id, 'delivery-note').pipe(take(1)).subscribe({
      next: (url) => window.open(url, '_blank'),
      error: () => this.error = 'The delivery note could not be printed. Please try again.',
    });
  }
}
